/**
 * Connectivity detection.
 *
 * `navigator.onLine` is necessary but not sufficient: it only reports whether
 * the OS has *a* network interface, not whether the *server* is reachable
 * (captive portals, dropped tunnels, server down — "networks lie"). So we
 * combine two signals:
 *
 *  1. **`navigator.onLine` + `online`/`offline` events** — instant, cheap,
 *     authoritative for the "no interface at all" case.
 *  2. **Heartbeat probe** — a periodic `HEAD` (or GET) against the endpoint.
 *     Confirms the server is *reachable*: that a request completes a full
 *     round-trip and returns *some* HTTP response (ANY status). Only runs while
 *     the page is visible to avoid waking radios in the background.
 *
 * Signal precedence — the heartbeat WINS. `navigator.onLine` is famously prone
 * to *false negatives*: split-tunnel VPNs, some corporate proxies, and several
 * OS/headless network stacks report `onLine === false` while the machine has
 * full connectivity. So when heartbeats are enabled, the composite is driven by
 * the heartbeat alone — a probe that *reached the server* (the fetch did not
 * throw) means online, regardless of what `navigator.onLine` claims. A non-2xx
 * status still means the server answered, so it stays online; the batch drain
 * (5xx/429 → backoff, 4xx → reject) owns what a non-2xx status *means*.
 * `navigator.onLine`'s `online`/`offline` events are used only as a fast TRIGGER
 * to re-probe immediately, never as a hard override — otherwise a false-negative
 * `onLine` would pin a fully-reachable app "Offline" forever (the live bug this
 * detector exists to prevent). Only when heartbeats are DISABLED
 * (`heartbeatIntervalMs <= 0`) is `navigator.onLine` the sole signal.
 * Connectivity is reachability, nothing more.
 */

import type { FetchImpl } from './types'

/** Detach a timer so the heartbeat never keeps the host process alive. */
function unrefTimer(handle: unknown): void {
	if (
		handle &&
		typeof handle === 'object' &&
		'unref' in handle &&
		typeof (handle as { unref: unknown }).unref === 'function'
	) {
		;(handle as { unref: () => void }).unref()
	}
}

export interface OnlineDetectorOptions {
	heartbeatUrl: string
	heartbeatIntervalMs: number
	fetchImpl: FetchImpl
	/** Called whenever the composite online state flips. */
	onChange: (online: boolean) => void
}

export class OnlineDetector {
	private navOnline = true
	private heartbeatOk = true
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null
	private started = false
	private composite = true

	constructor(private readonly opts: OnlineDetectorOptions) {
		const nav = (globalThis as { navigator?: { onLine?: boolean } }).navigator
		this.navOnline = nav?.onLine ?? true
		this.composite = this.navOnline
	}

	/** Current composite connectivity. */
	get online(): boolean {
		return this.composite
	}

	private readonly handleOnline = (): void => {
		this.navOnline = true
		// A regained interface is a strong signal — re-probe immediately so the
		// engine doesn't wait out a stale heartbeat interval.
		void this.probe()
		this.recompute()
	}

	private readonly handleOffline = (): void => {
		this.navOnline = false
		// A bare `offline` event is NOT proof the server is gone — `navigator.onLine`
		// false-negatives (VPNs, split tunnels, some OS stacks) fire it while the
		// machine is fully reachable. Re-probe: the heartbeat is authoritative, so a
		// still-reachable server keeps us online; a genuine drop makes the probe
		// throw → offline. Without this, a spurious event pinned the app "Offline".
		void this.probe()
		this.recompute()
	}

	private recompute(): void {
		// The heartbeat is authoritative when enabled: a probe that reached the
		// server means online regardless of `navigator.onLine` (which false-negatives
		// on VPNs/proxies). Only when heartbeats are disabled does `navigator.onLine`
		// stand alone.
		const next =
			this.opts.heartbeatIntervalMs <= 0 ? this.navOnline : this.heartbeatOk
		if (next !== this.composite) {
			this.composite = next
			this.opts.onChange(next)
		}
	}

	private async probe(): Promise<void> {
		if (this.opts.heartbeatIntervalMs <= 0) return
		try {
			// A completed round-trip — of ANY status — proves the server is
			// reachable, the only thing connectivity detection asks. We ignore the
			// status code on purpose: a 404 (HEAD on a POST-only route), a 401/403
			// (auth-gated probe), or a 5xx (up-but-erroring) all mean the server
			// ANSWERED. The heartbeat is "offline" strictly when `fetchImpl` throws
			// — no response at all: DNS/TCP/TLS failure, or a CORS/CSP block.
			// Treating a non-2xx reply as offline is what pinned a same-origin app
			// "Offline" forever when its heartbeat hit a 404 route.
			await this.opts.fetchImpl(this.opts.heartbeatUrl, {
				method: 'HEAD',
				cache: 'no-store',
			})
			this.heartbeatOk = true
		} catch {
			this.heartbeatOk = false
		}
		this.recompute()
	}

	/** Begin listening for events and (optionally) heartbeat probing. */
	start(): void {
		if (this.started) return
		this.started = true
		const target = globalThis as unknown as {
			addEventListener?: (t: string, l: () => void) => void
		}
		target.addEventListener?.('online', this.handleOnline)
		target.addEventListener?.('offline', this.handleOffline)
		if (this.opts.heartbeatIntervalMs > 0) {
			void this.probe()
			this.heartbeatTimer = setInterval(
				() => void this.probe(),
				this.opts.heartbeatIntervalMs,
			)
			unrefTimer(this.heartbeatTimer)
		}
	}

	/** Stop all listeners and timers. */
	stop(): void {
		if (!this.started) return
		this.started = false
		const target = globalThis as unknown as {
			removeEventListener?: (t: string, l: () => void) => void
		}
		target.removeEventListener?.('online', this.handleOnline)
		target.removeEventListener?.('offline', this.handleOffline)
		if (this.heartbeatTimer !== null) {
			clearInterval(this.heartbeatTimer)
			this.heartbeatTimer = null
		}
	}

	/** Force an immediate heartbeat probe (used on visibility resume). */
	async forceProbe(): Promise<void> {
		await this.probe()
	}
}
